import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl, formatDate } from "../../lib/api";
import { Zap, Search, User, ChevronRight, Wallet, ShoppingBag, CalendarClock } from "lucide-react";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";

const FILTERS = [
  ["all", "Todos"], ["open", "Saldo em aberto"], ["paid", "Quitados"],
  ["overdue", "Parcela vencida"], ["prom_pending", "Promissória pendente"],
  ["prom_signed", "Promissória assinada"], ["active", "Compra ativa"],
];

const STATUS_STYLE = {
  vencida: { dot: "🔴", cls: "bg-red-50 text-red-700", short: "Vencida" },
  parcial: { dot: "🟡", cls: "bg-amber-50 text-amber-700", short: "Parcial" },
  em_dia: { dot: "🟢", cls: "bg-emerald-50 text-emerald-700", short: "Em dia" },
  quitado: { dot: "🟢", cls: "bg-emerald-50 text-emerald-700", short: "Quitado" },
  sem_compras: { dot: "⚪", cls: "bg-gray-100 text-gray-500", short: "Sem compras" },
};

export default function EletrosBuyers({ admin = false }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [resellerFilter, setResellerFilter] = useState("all");
  const base = admin ? "/admin/eletros/compradores" : "/revenda/eletros/compradores";

  const load = async () => {
    const params = { filter };
    if (search.trim()) params.search = search.trim();
    if (admin) params.scope = "admin";
    const { data } = await api.get("/eletros/buyers", { params });
    setRows(data);
  };
  useEffect(() => { setRows(null); load().catch(() => setRows([])); /* eslint-disable-line */ }, [filter]);

  const onSearch = (e) => { e.preventDefault(); setRows(null); load().catch(() => setRows([])); };

  const resellers = useMemo(() => {
    if (!admin || !rows) return [];
    return Array.from(new Set(rows.map((r) => r.reseller_name).filter(Boolean))).sort();
  }, [rows, admin]);

  const shown = useMemo(() => {
    if (!rows) return [];
    if (admin && resellerFilter !== "all") return rows.filter((r) => r.reseller_name === resellerFilter);
    return rows;
  }, [rows, admin, resellerFilter]);

  return (
    <div className="space-y-5 p-4 lg:p-8" data-testid="eletros-buyers-page">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: GOLD }}><User size={24} className="text-[#2B1E4A]" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">{admin ? "Compradores Eletros" : "Meus compradores"}</h1>
          <p className="text-[12px] text-white/70">Ficha comercial e financeira de cada comprador</p>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex gap-2" data-testid="eletros-buyers-search-form">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="eletros-buyers-search-input"
            placeholder="Buscar por nome, telefone, CPF ou ID" className="w-full rounded-xl border border-border py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#5B2CA0]" />
        </div>
        <button type="submit" className="rounded-xl px-4 py-2.5 text-sm font-bold text-[#2B1E4A]" style={{ background: GOLD }} data-testid="eletros-buyers-search-btn">Buscar</button>
      </form>

      <div className="space-y-2" data-testid="eletros-buyers-filters">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([v, label]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`eletros-buyers-filter-${v}`}
              className={`rounded-full px-3 py-1.5 text-[12px] font-bold transition-colors ${filter === v ? "bg-[#5B2CA0] text-white" : "border border-border text-[#6B5A69] hover:bg-muted"}`}>
              {label}
            </button>
          ))}
        </div>
        {admin && resellers.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9A8A98]">Revendedora</span>
            <select value={resellerFilter} onChange={(e) => setResellerFilter(e.target.value)} data-testid="eletros-buyers-reseller-filter"
              className="w-full rounded-full border border-border px-3 py-1.5 text-[12px] font-bold text-[#6B5A69] outline-none focus:border-[#5B2CA0] sm:w-auto">
              <option value="all">Todas as revendedoras</option>
              {resellers.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
      </div>

      {rows === null ? (
        <div className="p-8 text-center text-sm text-[#9A8A98]">Carregando compradores...</div>
      ) : shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#D9CFE8] bg-[#FAF8FD] p-10 text-center" data-testid="eletros-buyers-empty">
          <Zap size={26} className="mx-auto mb-2 text-[#5B2CA0]" />
          <p className="text-sm text-[#6B5A69]">Nenhum comprador encontrado neste filtro.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="eletros-buyers-list">
          {shown.map((b) => {
            const s = STATUS_STYLE[b.status.code] || STATUS_STYLE.sem_compras;
            return (
              <button key={b.client_id} onClick={() => nav(`${base}/${b.client_id}`)} data-testid={`eletros-buyer-card-${b.client_id}`}
                className="flex min-w-0 flex-col rounded-2xl border border-border bg-white p-5 text-left transition-colors hover:border-[#5B2CA0]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-base font-bold text-brand-dark" title={b.name} data-testid="buyer-card-name">{b.name}</div>
                    <div className="truncate text-[12px] text-[#8A7788]">{b.phone || "sem telefone"}</div>
                    {admin && b.reseller_name && <div className="truncate text-[11px] text-[#9A8A98]">{b.reseller_name}</div>}
                  </div>
                  <span className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold ${s.cls}`} data-testid="buyer-card-status">{s.dot} {s.short}</span>
                </div>

                <div className="mt-4 space-y-2 border-t border-border pt-3 text-[13px]">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[#8A7788]">Compras</span>
                    <span className="font-bold text-brand-dark tabular-nums">{b.purchases}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[#8A7788]">Comprado</span>
                    <span className="whitespace-nowrap font-bold text-brand-dark tabular-nums">{brl(b.total_bought)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[#8A7788]">Saldo em aberto</span>
                    <span className="whitespace-nowrap font-bold tabular-nums" style={{ color: b.open_balance > 0 ? "#C9960A" : "#059669" }}>{brl(b.open_balance)}</span>
                  </div>
                </div>

                <div className="mt-3 space-y-1 border-t border-border pt-3 text-[12px]">
                  <div className="flex items-center gap-1.5 text-[#8A7788]">
                    <CalendarClock size={13} className="shrink-0" />
                    {b.next_due_date ? <span>Próx. vencimento {formatDate(b.next_due_date)}</span> : <span>{b.open_balance > 0 ? "Sem vencimento definido" : "Sem parcelas em aberto"}</span>}
                  </div>
                  <div className="font-semibold text-[#5B2CA0]">
                    {b.pending_promissories > 0 ? `⚠️ ${b.pending_promissories} promissória(s) pendente(s)` : "✓ Promissórias em dia"}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-1 border-t border-border pt-3 text-[13px] font-bold text-[#5B2CA0]" data-testid="buyer-card-view">
                  Ver perfil <ChevronRight size={15} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
